import { useState, type FormEvent } from 'react'
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { Button, InlineError, PhoneInput } from '../../components/ui'
import { useToast } from '../../hooks/useToast'
import {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyResetOtpMutation,
} from '../../services/authApi'
import { isNormalizedError } from '../../services/errorNormalizer'
import { AuthCard, AuthSplit } from './AuthCard'
import { OtpInput } from './OtpInput'
import { PasswordInput } from './PasswordInput'
import { isSendableMobile, maskMobile, SENDABLE_MOBILE_MESSAGE, toE164 } from '../../utils/phone'
import { RESEND_SECONDS, useResendCountdown } from './useResendCountdown'
import { useSession } from './useSession'

const MIN_PASSWORD = 8

interface ResetState {
  mobile: string
  otp: string
  /** Server-reported cooldown at the moment the code was sent. */
  resendIn: number
}

/**
 * H5 — `/{slug}/reset/*`: request → OTP → new password → done.
 *
 * OC-427: the code is checked on its own step (`auth/reset-password/verify`), so a
 * wrong or expired code is stopped there instead of after the new password is typed.
 * The check does not use the code up; it is held here and sent again with the new
 * password, and the API checks it once more.
 */
export function ResetRoutes() {
  const { slug } = useSession()
  const [state, setState] = useState<ResetState>({ mobile: '', otp: '', resendIn: RESEND_SECONDS })

  return (
    <Routes>
      <Route
        index
        element={<RequestStep slug={slug} onSent={(mobile, resendIn) => setState({ mobile, otp: '', resendIn })} />}
      />
      <Route
        path="verify"
        element={
          <CodeStep
            slug={slug}
            mobile={state.mobile}
            resendIn={state.resendIn}
            onCode={(otp) => setState((s) => ({ ...s, otp }))}
          />
        }
      />
      <Route path="password" element={<NewPasswordStep slug={slug} state={state} />} />
      <Route path="done" element={<DoneStep slug={slug} />} />
      <Route path="*" element={<Navigate to={`/${slug}/reset`} replace />} />
    </Routes>
  )
}

/* ------------------------------------------------------------------ step 1 */

function RequestStep({ slug, onSent }: { slug: string; onSent: (mobile: string, resendIn: number) => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [mobile, setMobile] = useState('')
  const [touched, setTouched] = useState(false)
  const [forgot, { isLoading, error }] = useForgotPasswordMutation()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!isSendableMobile(mobile)) return

    const normalized = toE164(mobile)
    const result = await forgot({ slug, mobile: normalized })
    if (!('data' in result)) return

    onSent(normalized, result.data?.resend_in ?? RESEND_SECONDS)
    // Dev builds echo the code back because no SMS gateway is wired yet.
    if (result.data?.otp) toast.show(`Verification code: ${result.data.otp}`, { duration: 0 })
    navigate(`/${slug}/reset/verify`)
  }

  return (
    <AuthSplit
      imageSide="right"
      title="Reset password"
      subtitle="Enter your phone number and we'll text you a code to reset your password securely."
      footer={
        <Link
          to={`/${slug}/signin`}
          className="font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
        >
          Return to sign in
        </Link>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-8">
        {error && <InlineError error={error} />}
        <PhoneInput
          label="Phone number"
          value={mobile}
          onChange={setMobile}
          error={touched && !isSendableMobile(mobile) ? SENDABLE_MOBILE_MESSAGE : undefined}
          required
        />
        <Button type="submit" fullWidth size="lg" loading={isLoading}>
          Send code
        </Button>
      </form>
    </AuthSplit>
  )
}

/* ------------------------------------------------------------------ step 2 */

function CodeStep({
  slug,
  mobile,
  resendIn,
  onCode,
}: {
  slug: string
  mobile: string
  resendIn: number
  onCode: (otp: string) => void
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const [otp, setOtp] = useState('')
  const [verify, { isLoading: verifying, error: verifyError, reset: clearVerify }] = useVerifyResetOtpMutation()
  const [resend, { isLoading: resending, error: resendError, reset: clearResend }] = useForgotPasswordMutation()
  const [seconds, setSeconds] = useResendCountdown(resendIn)

  if (!mobile) return <Navigate to={`/${slug}/reset`} replace />

  const error = resendError ?? verifyError

  const go = async (code: string) => {
    if (code.length !== 6 || verifying) return
    clearResend()
    const result = await verify({ slug, mobile, otp: code })
    if ('error' in result) return
    onCode(code)
    navigate(`/${slug}/reset/password`)
  }

  // A refused resend used to vanish silently (the screen counted 30s, the API
  // enforces 60s). Its error is shown now, and the countdown follows the server.
  const resendCode = async () => {
    clearVerify()
    const result = await resend({ slug, mobile })
    if ('error' in result) {
      if (isNormalizedError(result.error) && result.error.retryAfter) setSeconds(result.error.retryAfter)
      return
    }
    setOtp('')
    setSeconds(result.data?.resend_in ?? RESEND_SECONDS)
    if (result.data?.otp) toast.show(`Verification code: ${result.data.otp}`, { duration: 0 })
    else toast.success('We sent a new code.')
  }

  return (
    <AuthCard
      icon="smartphone"
      title="Check your phone"
      subtitle={
        <>
          We&apos;ve sent a 6-digit verification code to{' '}
          <strong className="font-medium text-on-surface">{maskMobile(mobile)}</strong>. Enter it below to confirm
          your identity.
        </>
      }
      footer={
        <Link
          to={`/${slug}/reset`}
          className="inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
            arrow_back
          </span>
          Use a different number
        </Link>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void go(otp)
        }}
        className="space-y-8"
      >
        {error && <InlineError error={error} />}
        <OtpInput
          value={otp}
          onChange={setOtp}
          onComplete={(code) => void go(code)}
          error={Boolean(verifyError)}
          disabled={verifying}
        />
        <Button type="submit" fullWidth size="lg" loading={verifying} disabled={otp.length !== 6}>
          Verify and continue
        </Button>
        <p className="text-center font-body-md text-body-md text-on-surface-variant">
          {seconds > 0 ? (
            <>Didn&apos;t receive the code? Resend in {seconds}s</>
          ) : (
            <>
              Didn&apos;t receive the code?{' '}
              <Button variant="text" size="sm" onClick={() => void resendCode()} loading={resending}>
                Resend code
              </Button>
            </>
          )}
        </p>
      </form>
    </AuthCard>
  )
}

/* ------------------------------------------------------------------ step 3 */

function NewPasswordStep({ slug, state }: { slug: string; state: ResetState }) {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [touched, setTouched] = useState(false)
  const [reset, { isLoading, error }] = useResetPasswordMutation()

  if (!state.mobile || !state.otp) return <Navigate to={`/${slug}/reset`} replace />

  const passwordError =
    touched && password.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : undefined
  const confirmError = touched && confirm !== password ? 'These passwords don’t match.' : undefined

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (password.length < MIN_PASSWORD || confirm !== password) return

    const result = await reset({ slug, mobile: state.mobile, otp: state.otp, password })
    if (!('data' in result)) return
    navigate(`/${slug}/reset/done`, { replace: true })
  }

  return (
    <AuthCard title="Create a password" subtitle="Choose a strong password to protect your account.">
      <form onSubmit={submit} noValidate className="space-y-6">
        {error && (
          <div className="flex flex-col gap-2">
            <InlineError error={error} />
            <Link to={`/${slug}/reset/verify`} className="font-body-md text-sm text-primary hover:underline">
              Enter the code again
            </Link>
          </div>
        )}

        <PasswordInput
          label="New password"
          value={password}
          onChange={setPassword}
          error={passwordError}
          autoComplete="new-password"
          hint={`At least ${MIN_PASSWORD} characters.`}
          required
        />
        <PasswordInput
          label="Confirm password"
          value={confirm}
          onChange={setConfirm}
          error={confirmError}
          autoComplete="new-password"
          required
        />

        <div className="pt-2">
          <Button type="submit" fullWidth size="lg" loading={isLoading}>
            Save new password
          </Button>
        </div>
      </form>
    </AuthCard>
  )
}

/* ------------------------------------------------------------------ step 4 */

function DoneStep({ slug }: { slug: string }) {
  return (
    <AuthCard
      icon="check_circle"
      iconStyle="tile"
      title="Password updated"
      subtitle="Your password has been successfully changed. You can now securely sign in with your new credentials."
    >
      <Button to={`/${slug}/signin`} fullWidth size="lg">
        Sign in
      </Button>
    </AuthCard>
  )
}
